import React, { useRef, useEffect, useState, useCallback } from 'react';
import { playSound } from '../lib/soundEffects';

export default function GraphViewModal({ isOpen, onClose, items = [], folders = [], folderColors = {} }) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTagEdges, setShowTagEdges] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [nodeCount, setNodeCount] = useState(0);

  // Simulation state kept in refs for smooth 60fps rendering without react state lag
  const simRef = useRef({
    nodes: [],
    edges: [],
    tagEdges: [],
    draggedNode: null,
    transform: { x: 0, y: 0, scale: 1 },
    isPanning: false,
    panStart: { x: 0, y: 0 }
  });

  // Initialize graph nodes and edges
  const initGraph = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const centerX = width / 2;
    const centerY = height / 2;

    const nodes = [];
    const edges = [];
    const tagEdges = [];

    // Central Root Node
    const rootNode = {
      id: 'root',
      type: 'root',
      label: 'SOVEREIGN VAULT',
      x: centerX,
      y: centerY,
      vx: 0,
      vy: 0,
      radius: 26,
      color: '#10b981',
      glow: '#10b981',
    };
    nodes.push(rootNode);

    // Folder Hub Nodes
    const folderNodes = folders.map((f, i) => {
      const angle = (i / Math.max(folders.length, 1)) * Math.PI * 2;
      const dist = 180 + Math.random() * 40;
      const color = folderColors[f.id] || '#06b6d4';
      const fNode = {
        id: `folder-${f.id}`,
        type: 'folder',
        rawId: f.id,
        label: f.name,
        x: centerX + Math.cos(angle) * dist,
        y: centerY + Math.sin(angle) * dist,
        vx: 0,
        vy: 0,
        radius: 18,
        color: color,
        glow: color,
      };
      edges.push({ source: 'root', target: fNode.id, length: 180 });
      return fNode;
    });
    nodes.push(...folderNodes);

    // Bookmark Leaf Nodes
    const tagMap = {}; // tag -> [itemNodeIds]

    items.forEach((item, i) => {
      const parentFolderId = item.folder_id || item.folderId;
      const targetParentId = parentFolderId ? `folder-${parentFolderId}` : 'root';
      const angle = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * 80;

      const parentNode = nodes.find(n => n.id === targetParentId) || rootNode;

      const bNode = {
        id: `bm-${item.id}`,
        type: 'bookmark',
        rawItem: item,
        label: item.title || 'Untitled',
        url: item.url || '',
        tags: item.tags || [],
        clicks: item.clicks || 0,
        x: parentNode.x + Math.cos(angle) * dist,
        y: parentNode.y + Math.sin(angle) * dist,
        vx: 0,
        vy: 0,
        radius: Math.min(14, 8 + Math.sqrt(item.clicks || 1) * 1.5),
        color: parentFolderId ? (folderColors[parentFolderId] || '#3b82f6') : '#10b981',
        glow: '#3b82f6',
      };
      nodes.push(bNode);
      edges.push({ source: targetParentId, target: bNode.id, length: 70 });

      // Build tag map for cross-tag connections
      (item.tags || []).forEach(t => {
        const cleanT = t.toLowerCase().replace(/^#/, '');
        if (!tagMap[cleanT]) tagMap[cleanT] = [];
        tagMap[cleanT].push(bNode.id);
      });
    });

    // Generate tag cross edges (limit to avoid spider web chaos)
    Object.entries(tagMap).forEach(([tag, itemIds]) => {
      if (itemIds.length > 1 && itemIds.length <= 8) {
        for (let i = 0; i < itemIds.length - 1; i++) {
          tagEdges.push({
            source: itemIds[i],
            target: itemIds[i + 1],
            tag: tag
          });
        }
      }
    });

    simRef.current.nodes = nodes;
    simRef.current.edges = edges;
    simRef.current.tagEdges = tagEdges;
    simRef.current.transform = { x: 0, y: 0, scale: 0.9 };
    setNodeCount(nodes.length);
  }, [items, folders, folderColors]);

  useEffect(() => {
    if (!isOpen) return;
    initGraph();

    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const handleResize = () => {
      if (!canvasRef.current) return;
      canvasRef.current.width = window.innerWidth;
      canvasRef.current.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    // Physics Simulation Loop
    let running = true;

    const simulate = () => {
      if (!running) return;

      const { nodes, edges, transform, draggedNode } = simRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Force Step
      const nodeMap = {};
      nodes.forEach(n => { nodeMap[n.id] = n; });

      // 1. Repulsion between all nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const n1 = nodes[i];
          const n2 = nodes[j];
          const dx = n2.x - n1.x;
          const dy = n2.y - n1.y;
          const distSq = dx * dx + dy * dy + 1;
          const dist = Math.sqrt(distSq);

          if (dist < 350) {
            const force = (n1.type === 'root' || n2.type === 'root' ? 1200 : 400) / distSq;
            const fx = (dx / dist) * force;
            const fy = (dy / dist) * force;

            if (n1 !== draggedNode) {
              n1.vx -= fx;
              n1.vy -= fy;
            }
            if (n2 !== draggedNode) {
              n2.vx += fx;
              n2.vy += fy;
            }
          }
        }
      }

      // 2. Spring Attraction along Edges
      edges.forEach(e => {
        const s = nodeMap[e.source];
        const t = nodeMap[e.target];
        if (!s || !t) return;

        const dx = t.x - s.x;
        const dy = t.y - s.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const displacement = dist - e.length;
        const k = 0.04;
        const fx = (dx / dist) * displacement * k;
        const fy = (dy / dist) * displacement * k;

        if (s !== draggedNode && s.type !== 'root') {
          s.vx += fx;
          s.vy += fy;
        }
        if (t !== draggedNode && t.type !== 'root') {
          t.vx -= fx;
          t.vy -= fy;
        }
      });

      // 3. Central Gravitational Pull
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      nodes.forEach(n => {
        if (n !== draggedNode && n.type !== 'root') {
          n.vx += (cx - n.x) * 0.0005;
          n.vy += (cy - n.y) * 0.0005;
          // Damping
          n.vx *= 0.88;
          n.vy *= 0.88;
          n.x += n.vx;
          n.y += n.vy;
        }
      });

      // RENDER
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(transform.x, transform.y);
      ctx.scale(transform.scale, transform.scale);

      // Render Tree Edges
      edges.forEach(e => {
        const s = nodeMap[e.source];
        const t = nodeMap[e.target];
        if (!s || !t) return;

        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(t.x, t.y);
        ctx.strokeStyle = 'rgba(16, 185, 129, 0.18)';
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Render Cross-Tag Edges
      if (showTagEdges) {
        simRef.current.tagEdges.forEach(te => {
          const s = nodeMap[te.source];
          const t = nodeMap[te.target];
          if (!s || !t) return;

          ctx.beginPath();
          ctx.setLineDash([3, 4]);
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(t.x, t.y);
          ctx.strokeStyle = 'rgba(6, 182, 212, 0.25)';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);
        });
      }

      // Render Nodes
      nodes.forEach(n => {
        const isHovered = hoveredNode && hoveredNode.id === n.id;
        const isMatch = searchQuery ? n.label.toLowerCase().includes(searchQuery.toLowerCase()) : true;

        ctx.save();
        ctx.shadowColor = n.glow;
        ctx.shadowBlur = isHovered ? 25 : isMatch ? 12 : 3;

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius * (isHovered ? 1.3 : 1), 0, Math.PI * 2);
        ctx.fillStyle = isMatch ? n.color : '#334155';
        ctx.fill();

        ctx.lineWidth = isHovered ? 3 : 1.5;
        ctx.strokeStyle = isHovered ? '#ffffff' : 'rgba(255,255,255,0.4)';
        ctx.stroke();

        ctx.restore();

        // Node Label
        if (n.type === 'root' || n.type === 'folder' || isHovered || isMatch) {
          ctx.font = n.type === 'root' ? 'bold 12px monospace' : n.type === 'folder' ? 'bold 11px sans-serif' : '10px monospace';
          ctx.fillStyle = isHovered ? '#ffffff' : isMatch ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)';
          ctx.textAlign = 'center';
          ctx.fillText(n.label.slice(0, 18), n.x, n.y + n.radius + 14);
        }
      });

      ctx.restore();

      animFrameRef.current = requestAnimationFrame(simulate);
    };

    animFrameRef.current = requestAnimationFrame(simulate);

    return () => {
      running = false;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, initGraph, showTagEdges, hoveredNode, searchQuery]);

  // Convert screen coordinates to canvas world coordinates
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const { transform } = simRef.current;
    return {
      x: (e.clientX - rect.left - transform.x) / transform.scale,
      y: (e.clientY - rect.top - transform.y) / transform.scale
    };
  };

  const handleMouseDown = (e) => {
    const coords = getCanvasCoords(e);
    const { nodes } = simRef.current;

    // Check if clicked a node
    const clickedNode = nodes.find(n => {
      const dx = n.x - coords.x;
      const dy = n.y - coords.y;
      return dx * dx + dy * dy <= (n.radius + 4) * (n.radius + 4);
    });

    if (clickedNode) {
      playSound('blip');
      simRef.current.draggedNode = clickedNode;
    } else {
      simRef.current.isPanning = true;
      simRef.current.panStart = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseMove = (e) => {
    const coords = getCanvasCoords(e);
    const { nodes, isPanning, panStart, transform, draggedNode } = simRef.current;

    if (draggedNode) {
      draggedNode.x = coords.x;
      draggedNode.y = coords.y;
      draggedNode.vx = 0;
      draggedNode.vy = 0;
      return;
    }

    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      transform.x += dx;
      transform.y += dy;
      simRef.current.panStart = { x: e.clientX, y: e.clientY };
      return;
    }

    // Hover detection
    const found = nodes.find(n => {
      const dx = n.x - coords.x;
      const dy = n.y - coords.y;
      return dx * dx + dy * dy <= (n.radius + 6) * (n.radius + 6);
    });

    if (found !== hoveredNode) {
      if (found) playSound('blip');
      setHoveredNode(found || null);
    }
  };

  const handleMouseUp = (e) => {
    const { draggedNode } = simRef.current;
    if (draggedNode && draggedNode.type === 'bookmark') {
      const coords = getCanvasCoords(e);
      const dx = draggedNode.x - coords.x;
      const dy = draggedNode.y - coords.y;
      if (Math.abs(dx) < 3 && Math.abs(dy) < 3 && draggedNode.url) {
        playSound('launch');
        window.open(draggedNode.url, '_blank', 'noopener,noreferrer');
      }
    }
    simRef.current.draggedNode = null;
    simRef.current.isPanning = false;
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const { transform } = simRef.current;
    transform.scale = Math.max(0.3, Math.min(3, transform.scale * zoomFactor));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-neutral-950/95 flex flex-col backdrop-blur-md animate-in fade-in duration-200">
      {/* Top Cyber HUD Bar */}
      <div className="absolute top-4 left-4 right-4 z-10 flex flex-wrap items-center justify-between gap-3 pointer-events-none">
        {/* Left: Brand & Stats */}
        <div className="flex items-center gap-3 bg-neutral-900/90 border border-emerald-500/30 px-4 py-2 rounded-xl backdrop-blur-md pointer-events-auto shadow-xl">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold">
            🕸️
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xs font-bold text-white tracking-widest font-mono">SPATIAL CONSTELLATION GRAPH</h1>
              <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5 rounded font-mono">
                60 FPS PHYSICS
              </span>
            </div>
            <p className="text-[11px] text-neutral-400 font-mono">
              {nodeCount} Nodes • Folders & Tag Constellations • Drag to orbit, Click to open
            </p>
          </div>
        </div>

        {/* Center: Search & Filter */}
        <div className="flex items-center gap-2 bg-neutral-900/90 border border-neutral-800 px-3 py-1.5 rounded-xl backdrop-blur-md pointer-events-auto">
          <span className="text-neutral-500 text-xs">🔍</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Highlight node..."
            className="bg-transparent text-xs text-white placeholder-neutral-500 focus:outline-none w-36 font-mono"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-neutral-500 hover:text-white text-xs">
              ✕
            </button>
          )}
        </div>

        {/* Right: Controls & Close */}
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={() => setShowTagEdges(!showTagEdges)}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono border transition-all ${
              showTagEdges
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                : 'bg-neutral-900/80 text-neutral-400 border-neutral-800'
            }`}
          >
            {showTagEdges ? '✓ Tag Edges ON' : '○ Tag Edges OFF'}
          </button>

          <button
            onClick={initGraph}
            className="px-3 py-1.5 bg-neutral-900/80 hover:bg-neutral-800 text-neutral-300 border border-neutral-700/60 rounded-lg text-xs font-mono transition-all"
            title="Reset Graph Physics"
          >
            🔄 Reset
          </button>

          <button
            onClick={() => {
              playSound('blip');
              onClose();
            }}
            className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 rounded-lg text-xs font-mono transition-all"
          >
            ✕ Close (Esc)
          </button>
        </div>
      </div>

      {/* Interactive Physics Canvas */}
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        className="w-full h-full cursor-grab active:cursor-grabbing block"
      />

      {/* Hover Detail Card Floating Tooltip */}
      {hoveredNode && (
        <div className="absolute bottom-6 left-6 z-20 bg-neutral-900/95 border border-emerald-500/40 p-4 rounded-xl shadow-2xl backdrop-blur-xl max-w-sm pointer-events-none animate-in fade-in duration-100">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: hoveredNode.color }} />
            <span className="text-[10px] uppercase font-mono font-bold tracking-wider text-emerald-400">
              {hoveredNode.type}
            </span>
            {hoveredNode.clicks > 0 && (
              <span className="text-[10px] font-mono text-cyan-400 ml-auto">
                ⚡ {hoveredNode.clicks} hits
              </span>
            )}
          </div>
          <h3 className="text-sm font-bold text-white leading-tight mb-1">{hoveredNode.label}</h3>
          {hoveredNode.url && (
            <p className="text-xs text-neutral-400 font-mono truncate mb-2">{hoveredNode.url}</p>
          )}
          {hoveredNode.tags && hoveredNode.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {hoveredNode.tags.map((t, idx) => (
                <span key={idx} className="text-[10px] bg-neutral-800 text-neutral-300 px-1.5 py-0.5 rounded font-mono">
                  {t.startsWith('#') ? t : `#${t}`}
                </span>
              ))}
            </div>
          )}
          {hoveredNode.type === 'bookmark' && (
            <p className="text-[10px] text-emerald-400/80 font-mono mt-2">
              ➜ Click node to launch URL in new tab
            </p>
          )}
        </div>
      )}
    </div>
  );
}
